import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DOCUMENT,
  HostListener,
  inject,
  input,
  OnDestroy,
  signal,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { Footer } from '@components/footer/model';
import { navigationStyle } from '@components/navigation/config';
import { Navigation, NavigationStyle } from '@components/navigation/model';
import { PromotionService } from '@components/promotion/services';
import { StoresService } from '@components/stores-locator/services';
import { environment } from '@environments/environment';
import { CommonService } from '@services/common/common.service';
import { CommonMenu } from '@services/common/model';
import { DataLayerService } from '@services/data-layer';
import { ResponsiveService } from '@services/responsive';
import { SeoService } from '@services/seo';
import { JsonLD } from '@services/seo/model';
import { ButtonComponent } from '@shared/components/button';
import { Button } from '@shared/components/button/model';
import { MenuBurgerComponent } from '@shared/components/menu-burger';
import { SocialNetworksComponent } from '@shared/components/social-networks';
import { socialNetworksStyle } from '@shared/components/social-networks/config';
import { SocialNetworksStyle } from '@shared/components/social-networks/model';
import { RoutesPath } from '@shared/constants';
import { DynamicUrlDirective } from '@shared/directives';
import { Link } from '@shared/models';
import { ColorPipe } from '@shared/pipes';
import { resolveAbsoluteUrl } from '@shared/utils';
import { map, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-navigation',
  imports: [
    MenuBurgerComponent,
    ColorPipe,
    ButtonComponent,
    RouterLink,
    AsyncPipe,
    RouterLinkActive,
    SocialNetworksComponent,
    DynamicUrlDirective,
    NgClass,
  ],
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationComponent implements OnDestroy {
  readonly data = input.required<CommonMenu<Navigation | Footer>[]>();
  isOpen = signal(false);
  isScroll = signal(false);
  readonly breakpoints = inject(ResponsiveService).breakpoints;
  style = signal<NavigationStyle>(null!);
  socialNetworksStyle = signal<SocialNetworksStyle>(null!);
  cta = signal<Button>(null!);
  ctaSecondary = signal<Button>(null!);
  // The router encodes a "#" inside a link (%23), so an in-page anchor (#block_N) is scrolled to by onCtaClick.
  readonly ctaAnchor = computed(() => {
    const url = this.cta().url ?? '';
    return url.startsWith('#') ? url.slice(1) : null;
  });
  readonly #document = inject<Document>(DOCUMENT);
  readonly #window = this.#document.defaultView as Window;
  readonly #storesService = inject(StoresService);
  readonly #dataLayerService = inject(DataLayerService);
  dataNavigation$: Observable<Navigation> = inject(CommonService)
    .getNav$()
    .pipe(
      tap((menu) => {
        this.style.set(navigationStyle[menu.theme] ?? navigationStyle['WHITE']!);
        this.socialNetworksStyle.set(socialNetworksStyle[this.style().socialNetworksStyle]!);
      }),
      map((menu) => this.data().find((data) => data.id === menu.id)),
      map((nav) => {
        const n: CommonMenu<Navigation> = nav as CommonMenu<Navigation>;
        this.#setJsonLd(n?.data);
        this.cta.set({
          label: n?.data.cta[0]!.label ?? '',
          size: this.style().cta.size,
          url: n?.data.cta[0]!.url ?? '',
          theme: this.style().cta.theme ?? null,
        });
        const customerArea = n?.data.cta[1];
        this.ctaSecondary.set(
          customerArea
            ? {
                label: customerArea.label,
                size: this.style().cta.size,
                url: customerArea.url,
                theme: this.style().ctaSecondaryTheme,
              }
            : null!
        );
        this.#storesService.setUrlAppointment(this.cta().url ?? '');
        return n.data;
      })
    );
  readonly #seoService = inject(SeoService);
  readonly #promotionService = inject(PromotionService);
  readonly isPromotionBanner = this.#promotionService.banner;
  readonly isPromotionAllowed = this.#promotionService.isAllowed;
  protected readonly routeToHomepage = RoutesPath.HOME;

  @HostListener('window:scroll')
  onScroll() {
    this.isScroll.set(this.#window.scrollY > 0);
  }

  onOpen(value: boolean): void {
    this.isOpen.set(value);
    this.toggleScroll(!value);
  }

  onCtaClick(): void {
    this.onOpen(false);
    const anchor = this.ctaAnchor();
    const element = anchor ? this.#document.getElementById(anchor) : null;
    if (!element) return;
    const navHeight = this.breakpoints() === 'mobile' ? 80 : 72; // $s-nav-mobile-height, $s-nav-desktop-scroll-height
    this.#window.scrollTo({
      top: element.getBoundingClientRect().top + this.#window.scrollY - navHeight,
      behavior: 'smooth',
    });
  }

  onCustomerAreaClick(): void {
    this.onOpen(false);
    this.#dataLayerService.logEspaceClientEvent({
      event: 'acces_ec_cta',
      event_localisation: 'menu site principal',
      event_type: 'redirection',
    });
  }

  ngOnDestroy(): void {
    this.toggleScroll(true);
  }

  private toggleScroll(value: boolean) {
    if (value) {
      this.#document.documentElement.classList.remove('disabled-scrolling');
    } else {
      this.#document.documentElement.classList.add('disabled-scrolling');
    }
  }

  #setJsonLd(data: Navigation) {
    const jsonLd = this.#buildSiteNavigationJsonLd(data);
    this.#seoService.addJsonLdScript('navigation-principale', jsonLd);
  }

  #buildSiteNavigationJsonLd(data: Navigation): JsonLD {
    const nav = new Set<Link>([
      {
        label: 'Accueil',
        url: environment.hostname,
      },
      ...data.links.primary,
      ...data.cta,
      ...data.links.secondary,
    ]);
    const hasPart = Array.from(nav).map((item) => ({
      '@type': 'SiteNavigationElement',
      name: item.label,
      url: resolveAbsoluteUrl(item.url),
    }));

    return {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Navigation principale',
      url: environment.hostname,
      hasPart,
    };
  }
}
